import metricTypeCheckers from 'types/metricTypeCheckers';

/**
 * Groups a metric's own definition filters by the dataset they apply to.
 *
 * - Normal metric → `{ [metric.dataType]: [metric.filters] }`.
 * - Compound metric → its constituent normal metrics grouped by `dataType`, each
 *   dataset's value being that dataset's constituents' filters.
 *
 * Empty filter inputs are omitted. Used to apply the metric's own filters to the
 * per-dataset records fetch (on top of the driver + default dataset filters).
 */
const getMetricFiltersByDataset = ({
  metric,
  compoundMetricInputsLookup,
}: {
  metric: Metrics.Metric | undefined;
  compoundMetricInputsLookup: {
    [compoundMetricId: string]: Metrics.NormalMetric[] | undefined;
  };
}): Record<string, FilterInput[]> => {
  const result: Record<string, FilterInput[]> = {};
  if (!metric) {
    return result;
  }

  const add = (dataType: string, filters: FilterInput | undefined) => {
    if (!filters || Object.keys(filters).length === 0) {
      return;
    }
    result[dataType] = [...(result[dataType] ?? []), filters];
  };

  if (metricTypeCheckers.isCompoundMetric(metric)) {
    const constituents = compoundMetricInputsLookup[metric.id] ?? [];
    constituents.forEach((m) => add(m.dataType, m.filters));
  } else if (metricTypeCheckers.isNormalMetric(metric)) {
    add(metric.dataType, metric.filters);
  }

  return result;
};

export default getMetricFiltersByDataset;
